import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  return <section className="py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-97" data-mp="2b9x5kjlb8lhb">
      <div className="mx-auto max-w-6xl px-4" data-id="element-98" data-mp="269j6hr1oslvf">
        <div className="text-center" data-id="element-99" data-mp="268rcgknqmw79">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-celeste" data-id="element-100" data-mp="21apr1t2uaksm">Guarda dentro</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl" data-id="element-101" data-mp="1wceirms70ici">
            Scopri cosa c&apos;è dentro…
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-102" data-mp="3qqz13st1k1sq">
            <span className="h-1 w-10 rounded-full bg-gold" data-id="element-103" data-mp="1p0gjysde4000" />
            <span className="h-1 w-3 rounded-full bg-celeste shadow-[0_0_10px_rgba(94,200,242,0.6)]" data-id="element-104" data-mp="1trgiu362ks55" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-mist" data-id="element-105" data-mp="1f32iq001fhsa">
            Pagine visive con infografiche, schemi chiari, punti chiave, errori comuni degli studenti e attività pronte per la
            classe. Ecco alcune pagine reali del Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carosello" aria-label="Pagine di esempio" data-id="element-106" data-mp="2cq2kpk9euna4">
          <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [scrollbar-width:none] [-webkit-overflow-scrolling:touch] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-107" data-mp="1yrb9c5lxnfpo">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-ink/55 shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-celeste/70 opacity-100' : 'border-celeste/20 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} di ${total}`} data-id="element-108" data-mp="rdhlqd71uk00" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-109" data-mp="osjvxmen4a6d" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className="absolute left-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-celeste/50 bg-ink/80 text-white shadow-lg shadow-black/40 backdrop-blur-md transition-colors duration-150 ease-out hover:bg-abyss focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:-left-2" aria-label="Pagina precedente" data-id="element-110" data-mp="10mkvxj0lm6b">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-111" data-mp="39japcdvkj0lj" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className="absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-celeste/50 bg-ink/80 text-white shadow-lg shadow-black/40 backdrop-blur-md transition-colors duration-150 ease-out hover:bg-abyss focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 sm:-right-2" aria-label="Pagina successiva" data-id="element-112" data-mp="2crnq3ctz96t3">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-113" data-mp="2vgu2ki46h26g" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-114" data-mp="30fndq3vzsh3g">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-celeste ${index === active ? 'w-6 bg-celeste shadow-[0_0_10px_rgba(94,200,242,0.6)]' : 'w-2 bg-celeste/30 hover:bg-celeste/60'}`} aria-label={`Vai alla pagina ${index + 1}`} aria-current={index === active} data-id="element-115" data-mp="34jmt2j5jd9v8" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}